import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { BookableSlotWindowResponse } from "./contracts/BookableSlotWindowResponse";
import { BookableSlots } from "./models/BookableSlots";
import { fetchSlotWindows, saveSlotWindows } from "./slice-extensions/bookable-slots-slice-extension";
import { fetchPortalBookableSlots, requestPortalSlot } from "../portal/slice-extensions/portal-slots-slice-extension";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface BookableSlotsState {
  /** The signed-in instructor's published windows. */
  windows: BookableSlotWindowResponse[];
  windowsStatus: RequestStatus;
  /** Portal: the free slots per target member (self or ward). */
  portalSlotsByMemberId: Record<string, BookableSlots>;
}

const initialState: BookableSlotsState = {
  windows: [],
  windowsStatus: "idle",
  portalSlotsByMemberId: {},
};

const bookableSlotsSlice = createSlice({
  name: "bookableSlots",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSlotWindows.pending, (state) => {
        state.windowsStatus = "loading";
      })
      .addCase(fetchSlotWindows.fulfilled, (state, action) => {
        state.windows = action.payload;
        state.windowsStatus = "succeeded";
      })
      .addCase(fetchSlotWindows.rejected, (state) => {
        state.windowsStatus = "failed";
      })
      .addCase(saveSlotWindows.fulfilled, (state, action) => {
        state.windows = action.payload;
      })
      .addCase(fetchPortalBookableSlots.fulfilled, (state, action) => {
        state.portalSlotsByMemberId[action.payload.memberId] = action.payload.slots;
      })
      .addCase(requestPortalSlot.fulfilled, (state, action) => {
        // The requested slot is no longer free; drop it until the list is read again.
        const { memberId, response } = action.payload;
        const slots = state.portalSlotsByMemberId[memberId];
        if (!slots) return;
        slots.days = slots.days
          .map((day) => ({
            ...day,
            slots: day.slots.filter((s) => !(s.date === response.date && s.timeLocal === response.timeLocal)),
          }))
          .filter((day) => day.slots.length > 0);
      });
  },
});

export const selectSlotWindows = (state: RootState) => state.bookableSlots.windows;
export const selectSlotWindowsStatus = (state: RootState) => state.bookableSlots.windowsStatus;
export const selectPortalBookableSlots = (memberId: string) => (state: RootState): BookableSlots | undefined =>
  state.bookableSlots.portalSlotsByMemberId[memberId];

export default bookableSlotsSlice.reducer;
